/**
 * Window.jsx — Draggable, resizable window chrome.
 *
 * Header chrome: emoji + title + ⚙ settings + ➖ minimize + ⬜ maximize + ✖ close.
 * Focus glow: green hazy ring (Fissal emerald) when this window tops the focus stack.
 * Resize: 8 hit-zones around the perimeter.
 * Body: provided as children; widget bodies use container queries to reflow.
 */

import { createMemo, createSignal, createEffect, For, Show, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import wm from '../lib/wmStore.js';
import { snapDrag, snapResize, viewportSize, edgeSnapZone } from '../lib/snap.js';
import SettingsPanel from './SettingsPanel.jsx';
import { pickSetting } from '../lib/settingsLayers.js';
import {
  glassBorderless,
  glassChromeHidden,
  glassDataOnly,
  glassEnabled,
  restoreGlassChromeSettings,
} from '../lib/glassMode.js';
import { EMOJI } from '../lib/emoji.js';
import ContextMenu from './ContextMenu.jsx';
import './Window.css';

const HANDLES = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
const EDITABLE_CONTEXT_TYPES = new Set(['email', 'number', 'password', 'search', 'tel', 'text', 'url']);

function closestContextElement(target) {
  if (target?.nodeType === 1) return target;
  return target?.parentElement || null;
}

function isNativeWindowContextTarget(target) {
  const el = closestContextElement(target);
  if (!el) return false;
  if (el.closest('textarea, select, [contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]')) return true;
  const input = el.closest('input');
  if (!input) return false;
  return EDITABLE_CONTEXT_TYPES.has((input.getAttribute('type') || 'text').toLowerCase());
}

function hasSelectedTextAtTarget(target) {
  if (typeof window === 'undefined') return false;
  const selection = window.getSelection?.();
  if (!selection || selection.isCollapsed || !selection.toString().trim()) return false;
  const el = closestContextElement(target);
  if (!el) return true;
  for (let i = 0; i < selection.rangeCount; i += 1) {
    try {
      if (selection.getRangeAt(i).intersectsNode(el)) return true;
    } catch { /* detached selection target */ }
  }
  return false;
}

export default function Window(props) {
  const { widget, meta } = props;
  const [showSettings, setShowSettings] = createSignal(false);
  const [settingsAnchor, setSettingsAnchor] = createSignal(null);
  const [contextMenu, setContextMenu] = createSignal(null);
  // Issue #118 — every window now exposes a settings panel (at minimum
  // the universal glass-mode toggle), so the gear is always available.
  const supportsSettings = createMemo(() => true);
  // #335/#345 — DOM ref for RAF-driven drag/resize and minimize animation.
  let windowRef;
  let settingsBtnRef;
  // #335 — animate minimize: scale/fade out before setting display:none.
  const [isMinimizing, setIsMinimizing] = createSignal(false);
  // #4 follow-up — plain (non-reactive) re-entry guard so the wm-driven
  // effect below can't double-fire the genie while it is already running.
  let minimizeAnimating = false;
  // Run the fly-to-taskbar genie, then commit the minimise. Driven by
  // wm.requestMinimize() so BOTH the header ▁ button and the taskbar button
  // animate identically (the taskbar path previously snapped instantly).
  function runMinimizeAnimation() {
    if (minimizeAnimating) return;
    minimizeAnimating = true;
    const prefersReduced = typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) { wm.endMinimize(widget.id, true); minimizeAnimating = false; return; }
    // #6 — fly the window toward its taskbar button. Compute the vector
    // from the window centre to the matching taskbar chip centre and feed
    // it to the CSS animation via --min-tx/--min-ty custom props. Falls
    // back to a downward genie when the chip can't be located.
    try {
      const winRect = windowRef?.getBoundingClientRect();
      const sel = `.v2-taskbar__win[data-wid="${CSS.escape(widget.id)}"]`;
      const chip = typeof document !== 'undefined' ? document.querySelector(sel) : null;
      const chipRect = chip?.getBoundingClientRect();
      if (winRect && windowRef) {
        const winCx = winRect.left + winRect.width / 2;
        const winCy = winRect.top + winRect.height / 2;
        const tx = chipRect ? (chipRect.left + chipRect.width / 2) - winCx : 0;
        const ty = chipRect
          ? (chipRect.top + chipRect.height / 2) - winCy
          : (typeof window !== 'undefined' ? window.innerHeight : 800) - winCy;
        windowRef.style.setProperty('--min-tx', `${Math.round(tx)}px`);
        windowRef.style.setProperty('--min-ty', `${Math.round(ty)}px`);
      }
    } catch { /* best-effort animation only */ }
    setIsMinimizing(true);
    // 220ms = --t-mid-ish fly-out; matches the .is-minimizing CSS duration.
    setTimeout(() => {
      wm.endMinimize(widget.id, true);
      setIsMinimizing(false);
      minimizeAnimating = false;
      if (windowRef) {
        windowRef.style.removeProperty('--min-tx');
        windowRef.style.removeProperty('--min-ty');
      }
    }, 220);
  }
  // React to minimise requests from either control (the header ▁ button and
  // the taskbar button both call wm.requestMinimize). Reads only the store
  // flag so the effect re-runs exactly on request/clear; the boolean guard
  // above blocks re-entry.
  createEffect(() => {
    if (wm.isMinimizing(widget.id)) runMinimizeAnimation();
  });
  createEffect(() => {
    if (widget.minimized && showSettings()) setShowSettings(false);
  });
  const focused = createMemo(() => {
    const order = wm.state.focusOrder;
    return order.length && order[order.length - 1] === widget.id;
  });
  const z = createMemo(() => wm.zOf(widget.id));
  const isGlass = createMemo(() => glassEnabled(widget));
  const isGlassBorderless = createMemo(() => glassBorderless(widget));
  const isGlassDataOnly = createMemo(() => glassDataOnly(widget));
  const hasHiddenGlassChrome = createMemo(() => glassChromeHidden(widget));
  const titleFilters = createMemo(() => {
    const filters = props.titleChrome?.filters;
    return Array.isArray(filters) ? filters.filter(Boolean) : [];
  });

  function otherWindows() {
    const dash = wm.state.dashboards.find(d => d.id === wm.state.activeDashboardId);
    return (dash?.widgets || []).filter(w => w.id !== widget.id);
  }

  function startDrag(ev) {
    if (ev.button !== 0) return;
    if (widget.maximized) return;
    ev.preventDefault();
    wm.focus(widget.id);
    if (windowRef) windowRef.classList.add('is-dragging');
    const startX = ev.clientX, startY = ev.clientY;
    const ox = widget.x, oy = widget.y;

    // Issue #43 — Spacebar opt-in snapping. Default = free placement.
    let snapDown = false;
    const hint = _createSnapHint();

    function onKeyDown(e) {
      if (e.code === 'Space') { e.preventDefault(); snapDown = true;  _updateSnapHint(hint, true); }
    }
    function onKeyUp(e) {
      if (e.code === 'Space') { snapDown = false; _updateSnapHint(hint, false); }
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup',   onKeyUp);

    // #345 — RAF throttle: bypass reactive store during drag; commit only on pointerup.
    let rafId = null;
    let pendingPos = null;
    // #5 — live Windows-style edge/corner snap preview.
    let pendingSnap = null;
    const preview = _createSnapPreview();
    function move(e) {
      // #336/#382 — clamp inside the desktop viewport. Its CSS top/bottom
      // already accounts for SpyBar/top chrome and the docked taskbar.
      const viewport = viewportSize();
      const maxY = Math.max(0, viewport.h - 32);
      const box = {
        x: Math.max(0, ox + (e.clientX - startX)),
        y: Math.max(0, Math.min(maxY, oy + (e.clientY - startY))),
        w: widget.w, h: widget.h,
      };
      const out = snapDown ? snapDrag(box, otherWindows(), viewport) : box;
      pendingPos = { x: out.x, y: out.y };
      // #5 — edge/corner snap follows the live cursor (OS behaviour). Takes
      // precedence over free placement when a zone is engaged on release.
      const viewportRect = _desktopViewportRect();
      pendingSnap = edgeSnapZone({
        x: e.clientX - viewportRect.x,
        y: e.clientY - viewportRect.y,
      }, viewport);
      _updateSnapPreview(preview, pendingSnap);
      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          if (pendingPos && windowRef) {
            windowRef.style.transform = `translate3d(${pendingPos.x}px, ${pendingPos.y}px, 0)`;
          }
        });
      }
    }
    function up() {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      if (pendingSnap) {
        wm.snapWindow(widget.id, pendingSnap.rect, pendingSnap.maximize);
      } else if (pendingPos) {
        if (windowRef) {
          windowRef.style.transform = `translate3d(${pendingPos.x}px, ${pendingPos.y}px, 0)`;
        }
        wm.setGeometry(widget.id, { x: pendingPos.x, y: pendingPos.y });
      }
      pendingPos = null;
      pendingSnap = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup',   up);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup',   onKeyUp);
      if (windowRef) windowRef.classList.remove('is-dragging');
      _removeSnapHint(hint);
      _removeSnapPreview(preview);
    }
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup',   up);
  }

  function startResize(handle) {
    return (ev) => {
      if (ev.button !== 0) return;
      if (widget.maximized) return;
      ev.preventDefault();
      ev.stopPropagation();
      wm.focus(widget.id);
      if (windowRef) windowRef.classList.add('is-resizing');
      const startX = ev.clientX, startY = ev.clientY;
      const ow = widget.w, oh = widget.h, ox = widget.x, oy = widget.y;

      // Issue #43 — Spacebar opt-in snapping for resize too.
      let snapDown = false;
      const hint = _createSnapHint();
      function onKeyDown(e) {
        if (e.code === 'Space') { e.preventDefault(); snapDown = true;  _updateSnapHint(hint, true); }
      }
      function onKeyUp(e) {
        if (e.code === 'Space') { snapDown = false; _updateSnapHint(hint, false); }
      }
      window.addEventListener('keydown', onKeyDown);
      window.addEventListener('keyup',   onKeyUp);

      // #345 — RAF throttle: update DOM directly during resize, commit on pointerup.
      let rafId = null;
      let pendingGeom = null;
      function move(e) {
        const dx = e.clientX - startX, dy = e.clientY - startY;
        const next = { w: ow, h: oh, x: ox, y: oy };
        if (handle.includes('e')) next.w = Math.max(220, ow + dx);
        if (handle.includes('s')) next.h = Math.max(140, oh + dy);
        if (handle.includes('w')) { next.w = Math.max(220, ow - dx); next.x = ox + (ow - next.w); }
        if (handle.includes('n')) { next.h = Math.max(140, oh - dy); next.y = oy + (oh - next.h); }
        const out = snapDown ? snapResize(next, handle, otherWindows(), viewportSize()) : next;
        pendingGeom = out;
        if (!rafId) {
          rafId = requestAnimationFrame(() => {
            rafId = null;
            if (pendingGeom && windowRef) {
              windowRef.style.transform = `translate3d(${pendingGeom.x}px, ${pendingGeom.y}px, 0)`;
              windowRef.style.width = `${pendingGeom.w}px`;
              windowRef.style.height = `${pendingGeom.h}px`;
            }
          });
        }
      }
      function up() {
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
        if (pendingGeom) {
          if (windowRef) {
            windowRef.style.transform = `translate3d(${pendingGeom.x}px, ${pendingGeom.y}px, 0)`;
            windowRef.style.width = `${pendingGeom.w}px`;
            windowRef.style.height = `${pendingGeom.h}px`;
          }
          wm.setGeometry(widget.id, pendingGeom);
          pendingGeom = null;
        }
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup',   up);
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('keyup',   onKeyUp);
        if (windowRef) windowRef.classList.remove('is-resizing');
        _removeSnapHint(hint);
      }
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    };
  }

  const style = createMemo(() => {
    // Issue #118 — glass mode: per-window opacity drives --widget-bg-opacity.
    // Slider is 0–100; clamp to 0.04–1.00 so floating glass widgets (e.g.
    // ESO Moons at 14%) blend into the desktop sky without vanishing.
    // Issue #341 — read via pickSetting so windowed/maximized layer overrides
    // are respected; falls back to the root value for backwards compat.
    const glassPct = Number(pickSetting(widget.settings, 'glassOpacity', widget.maximized));
    const opacity = Number.isFinite(glassPct)
      ? Math.max(0.04, Math.min(1, glassPct / 100))
      : 0.6;
    // #335 — keep window in DOM during fly-out animation; hide only after.
    if (widget.minimized && !isMinimizing()) return { display: 'none' };
    if (widget.maximized) {
      return {
        left: '8px', top: '8px',
        width: 'calc(100% - 16px)', height: 'calc(100% - 8px)',
        'z-index': 1000 + z(),
        '--widget-bg-opacity': opacity,
      };
    }
    // Clamp w/h on render to catalog minima so a corrupt row (issue #10:
    // initial render with sub-min sizes) still paints a usable window
    // until the user resizes / moves it.
    const min = meta?.sizes?.min || { w: 220, h: 140 };
    const w = Math.max(min.w, Number(widget.w) || min.w);
    const h = Math.max(min.h, Number(widget.h) || min.h);
    return {
      transform: `translate3d(${widget.x}px, ${widget.y}px, 0)`,
      width: w + 'px', height: h + 'px',
      'z-index': 1000 + z(),
      '--widget-bg-opacity': opacity,
    };
  });

  function updateSettingsAnchor() {
    if (typeof window === 'undefined') return;
    const rect = settingsBtnRef?.getBoundingClientRect?.() || windowRef?.getBoundingClientRect?.();
    if (!rect) return;
    const panelW = Math.min(420, Math.max(280, window.innerWidth - 16));
    const left = Math.min(
      Math.max(8, rect.right - panelW),
      Math.max(8, window.innerWidth - panelW - 8),
    );
    const anchorTop = settingsBtnRef ? rect.bottom + 8 : rect.top + 8;
    const top = Math.min(anchorTop, Math.max(8, window.innerHeight - 120));
    setSettingsAnchor({ left, top, width: panelW });
  }

  function toggleSettings() {
    if (showSettings()) {
      setShowSettings(false);
      return;
    }
    wm.focus(widget.id);
    updateSettingsAnchor();
    setShowSettings(true);
  }

  function openSettings() {
    if (!showSettings()) {
      wm.focus(widget.id);
      updateSettingsAnchor();
      setShowSettings(true);
    }
  }

  function openWindowContextMenu(e) {
    e.preventDefault();
    e.stopPropagation();
    wm.focus(widget.id);
    setContextMenu({ x: e.clientX, y: e.clientY });
  }

  function openBorderlessContextMenu(e) {
    if (!isGlassBorderless()) return;
    if (isNativeWindowContextTarget(e.target) || hasSelectedTextAtTarget(e.target)) return;
    openWindowContextMenu(e);
  }

  function openWindowContextMenuFromKeyboard(e) {
    if (e.key !== 'ContextMenu' && !(e.shiftKey && e.key === 'F10')) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    wm.focus(widget.id);
    setContextMenu({ x: rect.left + 28, y: rect.bottom + 6 });
  }

  const isLinkedToDesktopIcon = createMemo(() => wm.isWidgetLinkedToIcon(widget));

  function windowContextItems() {
    const linked = isLinkedToDesktopIcon();
    return [
      { icon: '◉', label: 'Focus window', onSelect: () => wm.focus(widget.id) },
      { icon: EMOJI.settings, label: 'Settings', iconTone: 'theme', onSelect: openSettings },
      {
        icon: '📌',
        label: linked ? 'Update desktop shortcut' : 'Pin shortcut to desktop',
        iconTone: 'theme',
        onSelect: () => wm.pinWindowToDesktop(widget.id),
      },
      ...hasHiddenGlassChrome() ? [{
        icon: '↺',
        label: 'Restore border and controls',
        onSelect: () => wm.setSettings(widget.id, restoreGlassChromeSettings(widget.settings || {})),
      }] : [],
      { separator: true },
      {
        icon: '▁',
        label: 'Minimize',
        disabled: !!widget.minimized,
        onSelect: () => wm.requestMinimize(widget.id),
      },
      {
        icon: widget.maximized ? '◱' : '□',
        label: widget.maximized ? 'Restore size' : 'Maximize',
        onSelect: () => wm.toggleMaximize(widget.id),
      },
      { separator: true },
      {
        icon: widget._permanent ? '◆' : '×',
        label: widget._permanent ? 'Pinned window' : 'Close window',
        disabled: !!widget._permanent,
        danger: !widget._permanent,
        onSelect: () => wm.closeWidget(widget.id),
      },
    ];
  }

  function handleOpenSettingsEvent(e) {
    if (e.detail?.widgetId !== widget.id) return;
    if (widget.minimized) wm.activateWindow(widget.id);
    else wm.focus(widget.id);
    requestAnimationFrame(() => openSettings());
  }

  onMount(() => {
    window.addEventListener('v2-open-window-settings', handleOpenSettingsEvent);
  });
  onCleanup(() => {
    window.removeEventListener('v2-open-window-settings', handleOpenSettingsEvent);
  });

  return (
    <div
      ref={el => windowRef = el}
      class="v2-window"
      classList={{
        'is-focused': focused(),
        'is-max': widget.maximized,
        'is-glass': isGlass(),
        'is-glass-borderless': isGlassBorderless(),
        'is-glass-data-only': isGlassDataOnly(),
        'is-minimizing': isMinimizing(),
      }}
      style={style()}
      onPointerDown={() => wm.focus(widget.id)}
      onContextMenu={openBorderlessContextMenu}
    >
      <Show when={!isGlassBorderless()}>
        <header class="v2-window__hdr"
          tabIndex="0"
          onPointerDown={startDrag}
          onDblClick={(e) => { if (e.target.closest('button')) return; wm.toggleMaximize(widget.id); }}
          onContextMenu={openWindowContextMenu}
          onKeyDown={openWindowContextMenuFromKeyboard}>
          <span class="v2-window__emoji">{meta?.emoji || EMOJI.window}</span>
          <span class="v2-window__title">{meta?.title || widget.widget_type}</span>
          <Show when={titleFilters().length}>
            <div class="v2-window__filters" aria-label="Active filters">
              <For each={titleFilters()}>{(filter) => (
                <button
                  type="button"
                  class="v2-window__filter-chip"
                  classList={{ 'is-muted': !!filter.muted }}
                  data-filter-key={filter.key || ''}
                  data-hint={filter.title || `${filter.label}: ${filter.value}`}
                  aria-label={filter.ariaLabel || `${filter.label}: ${filter.value}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); filter.onActivate?.(); }}
                >
                  <span class="v2-window__filter-label">{filter.label}</span>
                  <span class="v2-window__filter-value">{filter.value}</span>
                </button>
              )}</For>
            </div>
          </Show>
          <div class="v2-window__btns">
            <button
              class="v2-window__pin-icon"
              aria-label={isLinkedToDesktopIcon() ? 'Update desktop shortcut' : 'Pin shortcut to desktop'}
              classList={{ 'is-active': isLinkedToDesktopIcon() }}
              data-hint={isLinkedToDesktopIcon()
                ? 'Linked to a desktop shortcut.\nFilter, setting, and size changes auto-save to that icon.'
                : 'Pin this window to the desktop as a shortcut icon.\nPreserves its active filters, settings, and geometry.'}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => wm.pinWindowToDesktop(widget.id)}
            >📌</button>
            <Show when={supportsSettings()}>
              <button class="v2-window__settings" data-redfur-title="Settings" aria-label="Settings" classList={{ 'is-active': showSettings() }}
                data-hint={'Open this widget\u2019s settings panel.\nPer-widget options live here.'}
                ref={el => settingsBtnRef = el}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={toggleSettings}>⚙</button>
            </Show>
            <button data-redfur-title="Fullscreen Standalone View" aria-label="Fullscreen Standalone View"
              data-hint={'Open this widget in full-screen standalone view (no window chrome).'}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => { window.location.hash = `#${widget.widget_type}`; }}>⛶</button>
            <button data-redfur-title="Minimize" aria-label="Minimize"
              data-hint={'Minimise to the taskbar.\nClick the chip below to restore.'}
              data-hint-level="critical"
              onPointerDown={(e) => e.stopPropagation()} onClick={() => wm.requestMinimize(widget.id)}>▁</button>
            <button data-redfur-title={widget.maximized ? 'Restore' : 'Maximize'} aria-label={widget.maximized ? 'Restore' : 'Maximize'}
              data-hint={widget.maximized
                ? 'Restore to its previous size and position.'
                : 'Maximise to fill the desktop.\nDouble-click the header for the same.'}
              data-hint-level="critical"
              onPointerDown={(e) => e.stopPropagation()} onClick={() => wm.toggleMaximize(widget.id)}>{widget.maximized ? '◱' : '□'}</button>
            <Show when={!widget._permanent}>
              <button class="v2-window__close" data-redfur-title="Close" aria-label="Close"
                data-hint={'Close this widget.\nReopen it from the home menu (☰) → Widgets.'}
                data-hint-level="critical"
                onPointerDown={(e) => e.stopPropagation()} onClick={() => wm.closeWidget(widget.id)}>×</button>
            </Show>
          </div>
        </header>
      </Show>
      <Show when={isGlassBorderless() && !widget.maximized}>
        <button
          type="button"
          class="v2-window__borderless-drag"
          data-hint={'Drag window\nDouble-click to maximise · Right-click for window actions'}
          aria-label="Drag window"
          onPointerDown={(e) => { e.stopPropagation(); startDrag(e); }}
          onDblClick={(e) => { e.stopPropagation(); wm.toggleMaximize(widget.id); }}
        >
          ::
        </button>
      </Show>
      <Show when={showSettings()}>
        <SettingsPopover
          widget={widget}
          anchor={settingsAnchor}
          anchorButton={() => settingsBtnRef}
          onReposition={updateSettingsAnchor}
          onClose={() => setShowSettings(false)}
        />
      </Show>
      <Show when={contextMenu()}>
        <ContextMenu
          open={!!contextMenu()}
          x={contextMenu().x}
          y={contextMenu().y}
          label="Window actions"
          items={windowContextItems()}
          onClose={() => setContextMenu(null)}
        />
      </Show>
      <div class="v2-window__body">
        {props.children}
      </div>
      <Show when={!widget.maximized && !isGlassBorderless()}>
        {HANDLES.map(h => (
          <div class={`v2-window__handle v2-window__handle--${h}`} onPointerDown={startResize(h)} />
        ))}
      </Show>
    </div>
  );
}

let __settingsPopoverId = 0;

function SettingsPopover(props) {
  const titleId = `v2-settings-popover-title-${++__settingsPopoverId}`;
  let panelRef;
  let previousFocus;

  function style() {
    const anchor = props.anchor();
    if (!anchor) return {};
    return {
      left: `${anchor.left}px`,
      top: `${anchor.top}px`,
      width: `${anchor.width}px`,
      'max-height': `calc(100vh - ${anchor.top + 8}px)`,
    };
  }

  function focusFirst() {
    const focusable = panelRef?.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable?.[0];
    if (first) first.focus();
    else panelRef?.focus();
  }

  function handleKey(e) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      props.onClose?.();
      return;
    }
    if (e.key !== 'Tab' || !panelRef) return;
    const focusable = Array.from(panelRef.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    ));
    if (!focusable.length) {
      e.preventDefault();
      panelRef.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (!panelRef.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    }
  }

  function handleBackdrop(e) {
    if (e.target === e.currentTarget) props.onClose?.();
  }

  onMount(() => {
    previousFocus = document.activeElement;
    document.addEventListener('keydown', handleKey, true);
    window.addEventListener('resize', props.onReposition);
    window.addEventListener('scroll', props.onReposition, true);
    queueMicrotask(focusFirst);
  });
  onCleanup(() => {
    document.removeEventListener('keydown', handleKey, true);
    window.removeEventListener('resize', props.onReposition);
    window.removeEventListener('scroll', props.onReposition, true);
    const fallback = props.anchorButton?.();
    if (previousFocus?.isConnected) previousFocus.focus();
    else fallback?.focus?.();
  });

  return (
    <Portal>
      <div class="v2-settings-popover__backdrop" onPointerDown={handleBackdrop}>
        <div
          ref={el => panelRef = el}
          class="v2-settings-popover"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex="-1"
          style={style()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <span id={titleId} class="v2-settings-popover__sr-title">Widget settings</span>
          <SettingsPanel widget={props.widget} onClose={props.onClose} />
        </div>
      </div>
    </Portal>
  );
}

// ── Issue #43: Snap hint helpers (pure DOM — no extra component) ─────────
// A fixed overlay hint that appears during drag/resize and updates based on
// whether Space is currently held.

function _createSnapHint() {
  const el = document.createElement('div');
  el.className = 'v2-snap-hint';
  el.setAttribute('aria-hidden', 'true');
  el.textContent = 'SPACE to snap';
  document.body.appendChild(el);
  return el;
}

function _updateSnapHint(el, active) {
  if (!el) return;
  el.textContent = active ? '⚡ snapping' : 'SPACE to snap';
  el.classList.toggle('v2-snap-hint--active', active);
}

function _removeSnapHint(el) {
  el?.remove();
}

// ── #5: Windows-style edge/corner snap preview overlay ───────────────────
// A single fixed rectangle that morphs to the target zone while dragging.
function _createSnapPreview() {
  const el = document.createElement('div');
  el.className = 'v2-snap-preview';
  el.setAttribute('aria-hidden', 'true');
  document.body.appendChild(el);
  return el;
}

function _desktopViewportRect() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { x: 0, y: 0, w: 0, h: 0 };
  }
  const desktop = document.querySelector('.v2-desktop');
  const rect = desktop?.getBoundingClientRect();
  if (rect && rect.width > 0 && rect.height > 0) {
    return { x: rect.left, y: rect.top, w: rect.width, h: rect.height };
  }
  return { x: 0, y: 0, w: window.innerWidth, h: Math.max(0, window.innerHeight - 48) };
}

function _updateSnapPreview(el, zone) {
  if (!el) return;
  if (!zone) { el.classList.remove('is-active'); return; }
  const viewport = _desktopViewportRect();
  const r = zone.maximize
    ? { x: 0, y: 0, w: viewport.w, h: viewport.h }
    : zone.rect;
  el.style.left   = `${viewport.x + r.x}px`;
  el.style.top    = `${viewport.y + r.y}px`;
  el.style.width  = `${r.w}px`;
  el.style.height = `${r.h}px`;
  el.classList.add('is-active');
}

function _removeSnapPreview(el) {
  el?.remove();
}
